// Stylised neon samurai silhouettes, one per Ronin coach.
// Pure SVG line-art, single `color` prop drives stroke + glow. Designed to
// sit beside a leg row (16–28px) or inside the coach selector (48–64px).

export function KurosawaIcon({ size = 24, color = "#9DF6FF", glow = true }) {
  // Meditating samurai: kabuto helmet dome, crossed legs, katana at waist.
  const filter = glow ? `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 8px ${color}77)` : "none";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden
      style={{ filter, display: "inline-block" }}>
      {/* Kabuto dome */}
      <path d="M20 22 Q32 10 44 22 L44 26 L20 26 Z" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      {/* Kabuto crest */}
      <path d="M28 16 L32 8 L36 16" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Face mask */}
      <path d="M22 26 L22 32 L42 32 L42 26" stroke={color} strokeWidth="1.3" fill="none"/>
      <line x1="25" y1="28.5" x2="29" y2="28.5" stroke={color} strokeWidth="1" strokeLinecap="round"/>
      <line x1="35" y1="28.5" x2="39" y2="28.5" stroke={color} strokeWidth="1" strokeLinecap="round"/>
      {/* Shoulders + arms (seated meditation) */}
      <path d="M14 38 Q18 34 24 34 L40 34 Q46 34 50 38" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      {/* Crossed legs base */}
      <path d="M14 54 L26 44 L38 44 L50 54 Z" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      {/* Katana (horizontal at waist) */}
      <line x1="10" y1="44" x2="26" y2="44" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="27" cy="44" r="1.3" fill={color}/>
    </svg>
  );
}

export function MifuneIcon({ size = 24, color = "#F6C643", glow = true }) {
  // Samurai with crossed katanas behind the head (duo-wield, balanced stance).
  const filter = glow ? `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 8px ${color}77)` : "none";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden
      style={{ filter, display: "inline-block" }}>
      {/* Crossed katanas behind */}
      <line x1="8" y1="8" x2="56" y2="56" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="56" y1="8" x2="8" y2="56" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      {/* Blade guards */}
      <circle cx="32" cy="32" r="3.6" stroke={color} strokeWidth="1.4" fill="var(--bc-panel, rgba(0,0,0,0.85))"/>
      {/* Kabuto */}
      <path d="M22 22 Q32 12 42 22 L42 26 L22 26 Z" stroke={color} strokeWidth="1.6" fill="rgba(0,0,0,0.8)" strokeLinejoin="round"/>
      <path d="M29 18 L32 12 L35 18" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Body/armor torso */}
      <path d="M22 30 L22 42 L42 42 L42 30" stroke={color} strokeWidth="1.6" fill="rgba(0,0,0,0.8)"/>
      {/* Chest plate lines */}
      <line x1="25" y1="34" x2="39" y2="34" stroke={color} strokeWidth="0.9"/>
      <line x1="25" y1="38" x2="39" y2="38" stroke={color} strokeWidth="0.9"/>
      {/* Legs */}
      <path d="M24 42 L22 54 M40 42 L42 54" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

export function NaruseIcon({ size = 24, color = "#FF4B5C", glow = true }) {
  // Samurai in attacking stance: katana raised above, forward-leaning body,
  // hakama mantle flowing behind.
  const filter = glow ? `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 8px ${color}77)` : "none";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden
      style={{ filter, display: "inline-block" }}>
      {/* Raised katana blade */}
      <line x1="44" y1="4" x2="28" y2="24" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
      <line x1="40" y1="8" x2="46" y2="2" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Katana guard */}
      <circle cx="27" cy="25" r="1.8" fill={color}/>
      {/* Kabuto (tilted forward) */}
      <path d="M18 26 Q28 18 36 24 L38 28 L20 32 Z" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      {/* Eye slit */}
      <line x1="24" y1="26" x2="30" y2="26" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Forward-leaning torso */}
      <path d="M22 32 L28 44 L44 44 L38 32 Z" stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
      {/* Mantle (hakama) behind */}
      <path d="M22 32 L12 48 L18 56 L28 44" stroke={color} strokeWidth="1.4" fill="none" strokeLinejoin="round" strokeLinecap="round"/>
      {/* Front leg (charging) */}
      <path d="M38 44 L46 58" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
      {/* Back leg (stance) */}
      <path d="M28 44 L24 58" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}

// Convenience: render the correct icon by coach id.
export function RoninIcon({ coach, size, color, glow }) {
  if (coach === "kurosawa") return <KurosawaIcon size={size} color={color} glow={glow} />;
  if (coach === "mifune")   return <MifuneIcon   size={size} color={color} glow={glow} />;
  if (coach === "naruse")   return <NaruseIcon   size={size} color={color} glow={glow} />;
  return null;
}
